import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { BookOpen, FileText, GraduationCap, Sparkles, Link as LinkIcon, X, StickyNote } from 'lucide-react';

const TitlePicker = ({ value, onChange, classification }) => {
  const [titles, setTitles] = useState([]);
  const [articleTitles, setArticleTitles] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isArticle = classification === '📝مقالة📝';

  useEffect(() => {
    api.get('/report-titles/filtered').then((r) => setTitles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isArticle) {
      api.get('/article-titles-global').then((r) => setArticleTitles(r.data)).catch(() => {});
    }
  }, [isArticle]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDelete = async (t) => {
    try {
      await api.delete(`/report-titles/${encodeURIComponent(t)}`);
      setTitles((prev) => prev.filter((x) => x !== t));
    } catch {}
  };

  // Combine user titles + global article titles, deduplicate
  const allTitles = isArticle ? [...new Set([...articleTitles, ...titles])] : titles;
  const filtered = allTitles.filter((t) => !value || t.includes(value));

  return (
    <div className="relative" ref={ref}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={isArticle ? 'ابحث أو ادخل عنوان المقالة' : 'ادخل عنوان الكتاب او المقالة'}
        className="h-12 text-lg border-2 focus:border-primary"
        data-testid="title-input"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-white border-2 border-primary/20 rounded-xl shadow-xl max-h-48 overflow-y-auto">
          {filtered.map((t) => {
            const isGlobal = isArticle && articleTitles.includes(t) && !titles.includes(t);
            return (
              <div key={t} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/50 cursor-pointer group">
                <span className="flex-1 truncate" onClick={() => { onChange(t); setOpen(false); }}>
                  {t}
                  {isGlobal && <span className="text-xs text-primary/60 mr-2">(مقالة عامة)</span>}
                </span>
                {titles.includes(t) && (
                  <button
                    type="button"
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-1 shrink-0"
                    onClick={(e) => { e.stopPropagation(); handleDelete(t); }}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NotesBox = ({ classification }) => {
  const [note, setNote] = useState('');
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setNote('');
    setLoaded(false);
    api.get(`/reading-notes/${encodeURIComponent(classification)}`)
      .then((r) => { setNote(r.data.note || ''); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [classification]);

  const saveNote = useCallback((text) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      api.put(`/reading-notes/${encodeURIComponent(classification)}`, { text }).catch(() => {});
    }, 800);
  }, [classification]);

  const handleChange = (e) => {
    const text = e.target.value;
    setNote(text);
    saveNote(text);
  };

  if (!loaded) return null;

  return (
    <div className="bg-amber-50/80 rounded-xl p-4 border border-amber-200/60">
      <div className="flex items-center gap-2 mb-2">
        <StickyNote className="w-4 h-4 text-amber-600" />
        <Label className="text-sm font-semibold text-amber-800">ملاحظاتي</Label>
        <span className="text-xs text-amber-500 mr-auto">حفظ تلقائي</span>
      </div>
      <textarea
        value={note}
        onChange={handleChange}
        placeholder="سجّل ملاحظاتك هنا... (أسئلة، أفكار، رقم الصفحة...)"
        className="w-full bg-white/70 border border-amber-200 rounded-lg p-3 text-sm min-h-[80px] resize-y focus:outline-none focus:border-amber-400 placeholder:text-amber-300"
        dir="rtl"
        data-testid={`notes-${classification}`}
      />
    </div>
  );
};

const ReportModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    classification: '',
    title: '',
    pages_read: '',
    is_completed: 'لا',
    total_pages: '',
    author: '',
    course_id: '',
    book_key: '',
    from_page: 0,
    to_page: '',
  });
  const [loading, setLoading] = useState(false);
  const [courseBooks, setCourseBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);

  const classifications = [
    { value: '📖كتاب📖', label: 'كتاب', icon: BookOpen },
    { value: '📝مقالة📝', label: 'مقالة', icon: FileText },
    { value: '📄دراسة علمية📄', label: 'دراسة علمية', icon: GraduationCap },
    { value: '✨التدبر✨', label: 'التدبر', icon: Sparkles },
    { value: '⛓️‍المساقات⛓️', label: 'المساقات', icon: LinkIcon },
  ];

  const isCourse = formData.classification === '⛓️‍المساقات⛓️';
  const showNotes = formData.classification && !isCourse;

  useEffect(() => {
    if (isCourse && courseBooks.length === 0) {
      api.get('/my-course-books').then((res) => setCourseBooks(res.data)).catch(() => {});
    }
  }, [isCourse]);

  useEffect(() => {
    if (formData.classification === '📝مقالة📝' && !formData.pages_read) {
      setFormData((prev) => ({ ...prev, pages_read: '5' }));
    }
  }, [formData.classification]);

  const handleBookSelect = (bookKey) => {
    const book = courseBooks.find((b) => b.book_key === bookKey);
    if (!book) return;
    setSelectedBook(book);
    setFormData((prev) => ({
      ...prev,
      title: book.title,
      course_id: book.course_id,
      book_key: book.book_key,
      author: book.author,
      total_pages: String(book.total_pages),
      from_page: book.last_page,
      to_page: '',
      pages_read: '',
      is_completed: 'لا',
    }));
  };

  const handleCurrentPageChange = (value) => {
    const currentPage = parseInt(value) || 0;
    const fromPage = selectedBook ? selectedBook.last_page : 0;
    const totalPages = selectedBook ? selectedBook.total_pages : 0;
    const pagesRead = Math.max(0, currentPage - fromPage);
    const isCompleted = totalPages > 0 && currentPage >= totalPages ? 'نعم' : 'لا';
    setFormData((prev) => ({ ...prev, to_page: value, pages_read: String(pagesRead), is_completed: isCompleted }));
  };

  const resetForm = () => {
    setFormData({ classification: '', title: '', pages_read: '', is_completed: 'لا', total_pages: '', author: '', course_id: '', book_key: '', from_page: 0, to_page: '' });
    setSelectedBook(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.classification) { toast.error('يرجى اختيار التصنيف'); return; }
    if (isCourse) {
      if (!selectedBook) { toast.error('يرجى اختيار الكتاب'); return; }
      const toPage = parseInt(formData.to_page) || 0;
      if (toPage <= selectedBook.last_page) { toast.error(`يجب أن تكون الصفحة أكبر من ${selectedBook.last_page}`); return; }
    } else {
      if (!formData.pages_read || parseInt(formData.pages_read) <= 0) { toast.error('يرجى إدخال عدد الصفحات المقروءة'); return; }
      if (formData.classification !== '✨التدبر✨' && !formData.title) { toast.error('يرجى إدخال العنوان'); return; }
    }
    if (!isCourse && formData.is_completed === 'نعم' && (!formData.total_pages || !formData.author)) { toast.error('يرجى إدخال عدد الصفحات والمؤلف'); return; }

    setLoading(true);
    try {
      const submitRes = await api.post('/reports/submit', {
        classification: formData.classification,
        title: formData.title || null,
        pages_read: parseInt(formData.pages_read) || 0,
        is_completed: formData.is_completed,
        total_pages: formData.total_pages ? parseInt(formData.total_pages) : 0,
        author: formData.author || null,
        course_id: formData.course_id || null,
        book_key: formData.book_key || null,
        from_page: formData.from_page || 0,
        to_page: parseInt(formData.to_page) || 0,
      });
      toast.success('تم ارسال التقرير بنجاح');
      const respData = submitRes?.data || {};
      const reportInfo = {
        is_completed: formData.is_completed,
        title: formData.title,
        author: formData.author,
        total_pages: formData.total_pages ? parseInt(formData.total_pages) : 0,
        classification: formData.classification,
        // Forward the celebration signals from the backend (course_completed,
        // book_completed, plus resolved titles) so the layout can show the
        // lavish course-completion modal at the right moment.
        book_completed: !!respData.book_completed,
        course_completed: !!respData.course_completed,
        completed_course_title: respData.completed_course_title || '',
        completed_book_title: respData.completed_book_title || '',
      };
      resetForm();
      onClose();
      if (onSuccess) onSuccess(reportInfo);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل ارسال التقرير');
    } finally {
      setLoading(false);
    }
  };

  const shouldShowCompletionButtons = formData.classification && formData.classification !== '✨التدبر✨' && !isCourse;
  const shouldShowExtraFields = shouldShowCompletionButtons && formData.is_completed === 'نعم';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto bg-white" data-testid="report-modal">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-accent text-center mb-2">التقرير القرائي اليومي</DialogTitle>
          <p className="text-center text-muted-foreground">سجل انجازك القرائي اليوم</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-6">
          {/* Classification */}
          <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/10">
            <Label className="text-lg font-bold mb-4 block text-accent">اختر نوع المقروء</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {classifications.map((cls) => {
                const Icon = cls.icon;
                const isSelected = formData.classification === cls.value;
                return (
                  <button
                    key={cls.value}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, classification: cls.value, title: '', pages_read: '', is_completed: 'لا', total_pages: '', author: '', course_id: '', book_key: '', from_page: 0, to_page: '' });
                      setSelectedBook(null);
                    }}
                    className={`p-5 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-3 hover:scale-105 ${
                      isSelected ? 'border-primary bg-gradient-to-br from-primary/20 to-primary/10 shadow-lg scale-105' : 'border-muted/50 hover:border-primary/50 hover:bg-muted/30'
                    }`}
                    data-testid={`classification-${cls.label}`}
                  >
                    <Icon className={`w-8 h-8 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`font-semibold text-sm ${isSelected ? 'text-primary' : 'text-accent'}`}>{cls.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes - for non-course classifications */}
          {showNotes && <NotesBox classification={formData.classification} />}

          {/* Course Book Dropdown */}
          {isCourse && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/10">
              <Label className="text-lg font-bold mb-3 block text-accent">اختر الكتاب</Label>
              {courseBooks.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">لم تنضم الى اي مساق بعد</p>
              ) : (
                <div className="space-y-2">
                  {courseBooks.map((book) => {
                    const isActive = book.is_unlocked && !book.is_completed;
                    return (
                      <button key={book.book_key} type="button" disabled={!isActive} onClick={() => isActive && handleBookSelect(book.book_key)}
                        className={`w-full text-right p-4 rounded-xl border-2 transition-all duration-200 ${
                          formData.book_key === book.book_key ? 'border-primary bg-primary/10 shadow-md' : isActive ? 'border-muted hover:border-primary/50 hover:bg-muted/30 cursor-pointer' : 'border-muted/30 bg-muted/10 opacity-50 cursor-not-allowed'
                        }`}
                        data-testid={`course-book-option-${book.book_key}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className={`font-bold ${isActive ? 'text-accent' : 'text-muted-foreground'}`}>{book.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">{book.course_title} - {book.author}</p>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full font-medium mr-3 ${
                            book.is_completed ? 'bg-green-100 text-green-700' : !book.is_unlocked ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
                          }`}>
                            {book.is_completed ? 'مختوم' : !book.is_unlocked ? 'مقفل' : 'متاح'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedBook && (
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-2">
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">المؤلف:</span><span className="font-semibold">{selectedBook.author}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">عدد الصفحات:</span><span className="font-semibold">{selectedBook.total_pages}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">آخر صفحة مسجلة:</span><span className="font-bold text-primary">{selectedBook.last_page}</span></div>
                </div>
              )}
            </div>
          )}

          {/* Title */}
          {formData.classification && !isCourse && formData.classification !== '✨التدبر✨' && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/10">
              <Label htmlFor="title" className="text-lg font-bold mb-3 block text-accent">العنوان</Label>
              <TitlePicker value={formData.title} onChange={(val) => setFormData({ ...formData, title: val })} classification={formData.classification} />
            </div>
          )}

          {/* Current Page - for courses */}
          {isCourse && selectedBook && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/10">
              <Label htmlFor="current-page" className="text-lg font-bold mb-3 block text-accent">الصفحة التي وصلت اليها</Label>
              <Input
                id="current-page"
                type="number"
                min={selectedBook.last_page + 1}
                max={selectedBook.total_pages}
                value={formData.to_page}
                onChange={(e) => handleCurrentPageChange(e.target.value)}
                placeholder={`من ${selectedBook.last_page + 1} الى ${selectedBook.total_pages}`}
                className="h-12 text-lg border-2 focus:border-primary text-center text-2xl font-bold text-primary no-spinners"
                data-testid="current-page-input"
              />
              {formData.pages_read && parseInt(formData.pages_read) > 0 && (
                <div className="mt-3 p-3 bg-secondary/10 rounded-lg text-center">
                  <span className="text-muted-foreground">عدد الصفحات المقروءة: </span>
                  <span className="text-2xl font-bold text-secondary">{formData.pages_read}</span>
                  {formData.is_completed === 'نعم' && <p className="text-green-600 font-bold mt-1">تم ختم الكتاب!</p>}
                </div>
              )}
            </div>
          )}

          {/* Pages Read - for non-course */}
          {formData.classification && !isCourse && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/10">
              <Label htmlFor="pages" className="text-lg font-bold mb-3 block text-accent">عدد الصفحات المقروءة</Label>
              <Input
                id="pages"
                type="number"
                min="1"
                value={formData.pages_read}
                onChange={(e) => setFormData({ ...formData, pages_read: e.target.value })}
                placeholder="مثال: 20"
                className="h-12 text-lg border-2 focus:border-primary text-center text-2xl font-bold text-primary no-spinners"
                data-testid="pages-read-input"
              />
            </div>
          )}

          {/* Completion Buttons */}
          {shouldShowCompletionButtons && (
            <div className="bg-white rounded-xl p-6 shadow-sm border-2 border-primary/10">
              <Label className="text-lg font-bold mb-4 block text-accent">هل انهيت المقروء؟</Label>
              <div className="grid grid-cols-2 gap-4">
                <button type="button" onClick={() => setFormData({ ...formData, is_completed: 'لا' })}
                  className={`py-4 px-6 rounded-xl border-2 font-bold text-lg transition-all duration-300 hover:scale-105 ${
                    formData.is_completed === 'لا' ? 'border-primary bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg scale-105' : 'border-muted hover:border-primary/50 text-accent'
                  }`}
                  data-testid="completion-no"
                >لا، ما زلت اقرأ</button>
                <button type="button" onClick={() => setFormData({ ...formData, is_completed: 'نعم' })}
                  className={`py-4 px-6 rounded-xl border-2 font-bold text-lg transition-all duration-300 hover:scale-105 ${
                    formData.is_completed === 'نعم' ? 'border-secondary bg-gradient-to-br from-secondary to-secondary/80 text-white shadow-lg scale-105' : 'border-muted hover:border-secondary/50 text-accent'
                  }`}
                  data-testid="completion-yes"
                >نعم، اتممته</button>
              </div>
            </div>
          )}

          {/* Extra Fields for Completed */}
          {shouldShowExtraFields && (
            <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl p-6 shadow-sm border-2 border-secondary/20 space-y-4">
              <div>
                <Label htmlFor="total_pages" className="text-lg font-bold mb-3 block text-accent">عدد صفحات الكتاب/المقروء</Label>
                <Input id="total_pages" type="number" min="1" value={formData.total_pages} onChange={(e) => setFormData({ ...formData, total_pages: e.target.value })} placeholder="اجمالي عدد الصفحات" className="h-12 text-lg border-2 focus:border-secondary no-spinners" data-testid="total-pages-input" />
              </div>
              <div>
                <Label htmlFor="author" className="text-lg font-bold mb-3 block text-accent">اسم المؤلف</Label>
                <Input id="author" value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} placeholder="اسم مؤلف الكتاب" className="h-12 text-lg border-2 focus:border-secondary" data-testid="author-input" />
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => { resetForm(); onClose(); }} className="flex-1 h-14 text-lg border-2 hover:bg-muted" disabled={loading} data-testid="cancel-report-btn">الغاء</Button>
            <Button type="submit" className="flex-1 h-14 text-lg bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg" disabled={loading} data-testid="submit-report-btn">
              {loading ? 'جارٍ الارسال...' : 'ارسال التقرير'}
            </Button>
          </div>
        </form>

        <style>{`
          .no-spinners::-webkit-outer-spin-button,
          .no-spinners::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
          .no-spinners { -moz-appearance: textfield; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default ReportModal;
